import { useState, useMemo } from 'react'
import { useMaestroInsumos } from './useMaestroInsumos'
import type { InsumoMaestro, TipoInsumoMaestro } from '../presupuestos/tipos'
import styles from './ModalMaestroInsumos.module.css'

interface ModalMaestroInsumosProps {
  abierto: boolean
  alCerrar: () => void
}

const TIPO_LABELS: Record<TipoInsumoMaestro, { label: string; color: string; bg: string }> = {
  material: { label: 'Material', color: '#059669', bg: '#ecfdf5' },
  mano_obra: { label: 'Mano de Obra', color: '#7c3aed', bg: '#f5f3ff' },
  equipo: { label: 'Equipo', color: '#2563eb', bg: '#eff6ff' },
  transporte: { label: 'Transporte', color: '#d97706', bg: '#fffbeb' },
}

export function ModalMaestroInsumos({ abierto, alCerrar }: ModalMaestroInsumosProps) {
  const { insumos, actualizarInsumoEnCascada, crearInsumo, eliminarInsumo } = useMaestroInsumos()

  const [busqueda, setBusqueda] = useState('')
  const [filtroTipo, setFiltroTipo] = useState<string>('todos')
  const [editandoInsumo, setEditandoInsumo] = useState<InsumoMaestro | null>(null)
  const [nuevaTarifa, setNuevaTarifa] = useState<string>('')
  const [mensajeCascada, setMensajeCascada] = useState<string | null>(null)
  const [mostrarCrear, setMostrarCrear] = useState(false)

  // Formulario nuevo insumo
  const [nuevoForm, setNuevoForm] = useState<{
    codigo: string
    nombre: string
    tipo: TipoInsumoMaestro
    unidad: string
    tarifa_base: number
    categoria: string
  }>({
    codigo: '',
    nombre: '',
    tipo: 'material',
    unidad: 'UND',
    tarifa_base: 0,
    categoria: 'General',
  })

  const formatearCOP = (val: number) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
    }).format(val || 0)

  const insumosFiltrados = useMemo(() => {
    return insumos.filter((item) => {
      const matchTipo = filtroTipo === 'todos' || item.tipo === filtroTipo
      const q = busqueda.toLowerCase().trim()
      const matchBusqueda =
        !q ||
        item.codigo.toLowerCase().includes(q) ||
        item.nombre.toLowerCase().includes(q) ||
        item.categoria.toLowerCase().includes(q)
      return matchTipo && matchBusqueda
    })
  }, [insumos, filtroTipo, busqueda])

  const handleIniciarEdicion = (insumo: InsumoMaestro) => {
    setEditandoInsumo(insumo)
    setNuevaTarifa(String(insumo.tarifa_base))
    setMensajeCascada(null)
  }

  const handleGuardarTarifa = () => {
    if (!editandoInsumo) return
    const valorNumerico = parseFloat(nuevaTarifa.replace(/[^0-9.]/g, ''))
    if (isNaN(valorNumerico) || valorNumerico < 0) return

    const { apusAfectados } = actualizarInsumoEnCascada(editandoInsumo.id, valorNumerico)
    setMensajeCascada(
      `Tarifa actualizada para ${editandoInsumo.codigo}. Se recalcularon automáticamente ${apusAfectados} APUs dependientes en el banco.`
    )
    setEditandoInsumo(null)
  }

  const handleCrearInsumoSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!nuevoForm.codigo || !nuevoForm.nombre) return

    crearInsumo(nuevoForm)
    setMostrarCrear(false)
    setNuevoForm({
      codigo: '',
      nombre: '',
      tipo: 'material',
      unidad: 'UND',
      tarifa_base: 0,
      categoria: 'General',
    })
    setMensajeCascada('Insumo maestro creado exitosamente en el catálogo.')
  }

  if (!abierto) return null

  return (
    <div className={styles.overlay} onClick={alCerrar}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Cabecera */}
        <div className={styles.header}>
          <div>
            <div className={styles.badgeTop}>MAESTRO CENTRAL DE TARIFAS</div>
            <h2 className={styles.titulo}>Catálogo Maestro de Insumos y Mano de Obra</h2>
            <p className={styles.subtitulo}>
              Modifique aquí tarifas de materiales, cuadrillas, equipos y fletes. El sistema actualiza en cadena todos los APU dependientes.
            </p>
          </div>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.btnCrear}
              onClick={() => setMostrarCrear(!mostrarCrear)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>{mostrarCrear ? 'Cerrar Formulario' : 'Nuevo Insumo'}</span>
            </button>
            <button type="button" className={styles.btnCerrar} onClick={alCerrar} title="Cerrar modal">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Notificación de cascada */}
        {mensajeCascada && (
          <div className={styles.alertaCascada}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{mensajeCascada}</span>
            <button type="button" onClick={() => setMensajeCascada(null)} className={styles.cerrarAlerta}>
              ✕
            </button>
          </div>
        )}

        {/* Formulario Crear Insumo */}
        {mostrarCrear && (
          <form className={styles.formCrear} onSubmit={handleCrearInsumoSubmit}>
            <div className={styles.formGrid}>
              <div>
                <label className={styles.formLabel}>Código / Referencia</label>
                <input
                  type="text"
                  required
                  placeholder="Ej: COND-THHN-12"
                  value={nuevoForm.codigo}
                  onChange={(e) => setNuevoForm({ ...nuevoForm, codigo: e.target.value.toUpperCase() })}
                  className={styles.inputTexto}
                />
              </div>
              <div>
                <label className={styles.formLabel}>Tipo de Insumo</label>
                <select
                  value={nuevoForm.tipo}
                  onChange={(e) => setNuevoForm({ ...nuevoForm, tipo: e.target.value as TipoInsumoMaestro })}
                  className={styles.select}
                >
                  <option value="material">Material</option>
                  <option value="mano_obra">Mano de Obra (Tarifa/Día)</option>
                  <option value="equipo">Equipo y Herramienta</option>
                  <option value="transporte">Transporte y Flete</option>
                </select>
              </div>
              <div>
                <label className={styles.formLabel}>Unidad</label>
                <input
                  type="text"
                  required
                  placeholder="UND, M, ML, DIA, GL..."
                  value={nuevoForm.unidad}
                  onChange={(e) => setNuevoForm({ ...nuevoForm, unidad: e.target.value.toUpperCase() })}
                  className={styles.inputTexto}
                />
              </div>
              <div>
                <label className={styles.formLabel}>Tarifa Base ($ COP)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  placeholder="0"
                  value={nuevoForm.tarifa_base || ''}
                  onChange={(e) => setNuevoForm({ ...nuevoForm, tarifa_base: parseFloat(e.target.value) || 0 })}
                  className={styles.inputTexto}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className={styles.formLabel}>Nombre / Descripción Completa</label>
                <input
                  type="text"
                  required
                  placeholder="Descripción técnica del insumo o cargo..."
                  value={nuevoForm.nombre}
                  onChange={(e) => setNuevoForm({ ...nuevoForm, nombre: e.target.value })}
                  className={styles.inputTexto}
                />
              </div>
            </div>
            <div className={styles.formBotones}>
              <button type="button" className={styles.btnSecundario} onClick={() => setMostrarCrear(false)}>
                Cancelar
              </button>
              <button type="submit" className={styles.btnPrimario}>
                Guardar en Maestro
              </button>
            </div>
          </form>
        )}

        {/* Filtros y Búsqueda */}
        <div className={styles.controles}>
          <div className={styles.pestanas}>
            {[
              { id: 'todos', label: `Todos (${insumos.length})` },
              { id: 'material', label: 'Materiales' },
              { id: 'mano_obra', label: 'Mano de Obra' },
              { id: 'equipo', label: 'Equipos' },
              { id: 'transporte', label: 'Transportes' },
            ].map((p) => (
              <button
                key={p.id}
                type="button"
                className={`${styles.tabBtn} ${filtroTipo === p.id ? styles.tabActivo : ''}`}
                onClick={() => setFiltroTipo(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className={styles.buscador}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Buscar por código, nombre o categoría..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className={styles.inputBuscar}
            />
          </div>
        </div>

        {/* Tabla de Insumos */}
        <div className={styles.tablaContenedor}>
          <table className={styles.tabla}>
            <thead>
              <tr>
                <th style={{ width: '120px' }}>Tipo</th>
                <th style={{ width: '130px' }}>Código</th>
                <th>Nombre / Descripción</th>
                <th style={{ width: '80px', textAlign: 'center' }}>Und</th>
                <th style={{ width: '130px' }}>Categoría</th>
                <th style={{ width: '160px', textAlign: 'right' }}>Tarifa Base ($)</th>
                <th style={{ width: '110px', textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {insumosFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={7} className={styles.sinResultados}>
                    No se encontraron insumos maestros con el filtro actual.
                  </td>
                </tr>
              ) : (
                insumosFiltrados.map((it) => {
                  const infoTipo = TIPO_LABELS[it.tipo] || { label: it.tipo, color: '#475569', bg: '#f1f5f9' }
                  const estaEditando = editandoInsumo?.id === it.id

                  return (
                    <tr key={it.id} className={estaEditando ? styles.filaEditando : ''}>
                      <td>
                        <span
                          className={styles.badgeTipo}
                          style={{ color: infoTipo.color, backgroundColor: infoTipo.bg }}
                        >
                          {infoTipo.label}
                        </span>
                      </td>
                      <td className={styles.codigoText}>{it.codigo}</td>
                      <td className={styles.nombreText}>{it.nombre}</td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{it.unidad}</td>
                      <td className={styles.categoriaText}>{it.categoria}</td>
                      <td style={{ textAlign: 'right' }}>
                        {estaEditando ? (
                          <input
                            type="number"
                            step="0.01"
                            value={nuevaTarifa}
                            onChange={(e) => setNuevaTarifa(e.target.value)}
                            className={styles.inputInlineTarifa}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleGuardarTarifa()
                              if (e.key === 'Escape') setEditandoInsumo(null)
                            }}
                          />
                        ) : (
                          <span className={styles.tarifaValor}>{formatearCOP(it.tarifa_base)}</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {estaEditando ? (
                          <div className={styles.accionesInline}>
                            <button
                              type="button"
                              className={styles.btnGuardarMini}
                              onClick={handleGuardarTarifa}
                              title="Guardar tarifa y recalcular APUs en cascada"
                            >
                              ✓
                            </button>
                            <button
                              type="button"
                              className={styles.btnCancelarMini}
                              onClick={() => setEditandoInsumo(null)}
                              title="Cancelar"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div className={styles.accionesInline}>
                            <button
                              type="button"
                              className={styles.btnEditarMini}
                              onClick={() => handleIniciarEdicion(it)}
                              title="Editar tarifa base"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              className={styles.btnEliminarMini}
                              onClick={() => {
                                if (confirm(`¿Eliminar ${it.codigo} del maestro?`)) eliminarInsumo(it.id)
                              }}
                              title="Eliminar insumo"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pie de modal */}
        <div className={styles.footer}>
          <div className={styles.infoInsumos}>
            Total insumos maestros: <strong>{insumos.length}</strong> • Filtrados: <strong>{insumosFiltrados.length}</strong>
          </div>
          <button type="button" className={styles.btnCerrarModal} onClick={alCerrar}>
            Cerrar Maestro
          </button>
        </div>
      </div>
    </div>
  )
}

